import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	BarrelLayoutPass,
	CampaignData,
	CurrentDelayData,
	LastBillet,
	FurnaceBillet,
	PassChangeReason,
	ProductMillSetup,
	RecipeMeta,
	Rollset,
	WorkOrder,
} from "../models/CampaignData";
import type { StandData } from "../models/StandData";

// ─── Auto-refresh queries (every 5s) ────────────────────────────

export const getCampaignData = async (
	signal?: AbortSignal,
): Promise<{
	campaign: CampaignData | null;
	productMillSetup: ProductMillSetup[];
}> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];

	const resp = await apiService.callV2(
		"[ROLLS].[GetRollingMillSetupCampaignDataWeb]",
		parameters,
		signal,
	);

	if (resp.ok) {
		const campaign = get(
			resp,
			"data.tables[0].rows[0]",
			null,
		) as CampaignData | null;
		const productMillSetup = get(
			resp,
			"data.tables[1].rows",
			[],
		) as ProductMillSetup[];
		return { campaign, productMillSetup };
	}
	throw new Error(resp.message || "Error fetching campaign data");
};

export const getStandsData = async (
	signal?: AbortSignal,
): Promise<StandData[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];

	const resp = await apiService.callV2(
		"[ROLLS].[GetRollingMillSetupStandsDataWeb]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as StandData[];
	}
	throw new Error(resp.message || "Error fetching stands data");
};

export const getCurrentDelay = async (
	signal?: AbortSignal,
): Promise<CurrentDelayData | null> => {
	const apiService = new MESApiService();

	const resp = await apiService.callV2(
		"[MILL].[GetCurrentDelayWeb]",
		[],
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows[0]", null) as CurrentDelayData | null;
	}
	throw new Error(resp.message || "Error fetching current delay");
};

export const getFurnaceBillets = async (
	signal?: AbortSignal,
): Promise<FurnaceBillet[]> => {
	const apiService = new MESApiService();

	const resp = await apiService.callV2("[MILL].[GetNextBilletWeb]", [], signal);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as FurnaceBillet[];
	}
	throw new Error(resp.message || "Error fetching furnace billets");
};

// ─── Inline edits ────────────────────────────────────────────────

export const updateCampaign = async ({
	id,
	scheduledBillets,
	scheduledWeight,
}: {
	id: number;
	scheduledBillets?: number | null;
	scheduledWeight?: number | null;
}): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "id", value: id }];
	if (scheduledBillets !== undefined) {
		parameters.push({ name: "scheduledBillets", value: scheduledBillets });
	}
	if (scheduledWeight !== undefined) {
		parameters.push({ name: "scheduledWeight", value: scheduledWeight });
	}

	const resp = await apiService.callV2("[MILL].[UpdateCampaign]", parameters);
	if (!resp.ok) {
		throw new Error(resp.message || "Error updating campaign");
	}
};

export const resetTemporalBillets = async (): Promise<void> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[MILL].[ResetTemporalBilletsCount]",
		[],
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error resetting temporary billets");
	}
};

// ─── Modal actions ───────────────────────────────────────────────

export const startCampaign = async ({
	productSizeId,
	recipeId,
	scheduledBillets,
	scheduledWeight,
	workOrderName,
}: {
	productSizeId: number;
	recipeId: number | null;
	scheduledBillets: number;
	scheduledWeight: number;
	workOrderName: string | null;
}): Promise<number> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "productSizeId", value: productSizeId },
		{ name: "recipeId", value: recipeId },
		{ name: "scheduledBillets", value: scheduledBillets },
		{ name: "scheduledWeight", value: scheduledWeight },
		{ name: "workOrderName", value: workOrderName },
	];

	const resp = await apiService.callV2("[MILL].[StartCampaign]", parameters);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows[0].Id", 0) as number;
	}
	throw new Error(resp.message || "Error starting campaign");
};

export const validatePassChange = async ({
	standId,
	rollsetId,
	passPosition,
	productSizeId,
}: {
	standId: number;
	rollsetId: number;
	passPosition: number;
	productSizeId?: number;
}): Promise<string> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "standId", value: standId },
		{ name: "rollsetId", value: rollsetId },
		{ name: "passPosition", value: passPosition },
		{ name: "productSizeId", value: productSizeId ?? -1 },
	];

	const resp = await apiService.callV2(
		"[MILL].[ValidatePassChange]",
		parameters,
	);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows[0].Result", "SUCCESS") as string;
	}
	throw new Error(resp.message || "Error validating pass change");
};

export const changePass = async ({
	standId,
	rollsetId,
	passPosition,
	passChangeReasonId,
	comments,
}: {
	standId: number;
	rollsetId: number;
	passPosition: number;
	passChangeReasonId: number;
	comments?: string | null;
}): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "standId", value: standId },
		{ name: "rollsetId", value: rollsetId },
		{ name: "passPosition", value: passPosition },
		{ name: "passChangeReasonId", value: passChangeReasonId },
		{ name: "comments", value: comments ?? null },
	];

	const resp = await apiService.callV2("[MILL].[ChangePass]", parameters);
	if (!resp.ok) {
		throw new Error(resp.message || "Error changing pass");
	}
};

export const changeRollset = async ({
	standId,
	newRollsetId,
	newPassPosition,
}: {
	standId: number;
	newRollsetId: number | null;
	newPassPosition: number | null;
}): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "standId", value: standId },
		{ name: "newRollsetId", value: newRollsetId },
		{ name: "newPassPosition", value: newPassPosition },
	];

	const resp = await apiService.callV2("[ROLLS].[ChangeRollset]", parameters);
	if (!resp.ok) {
		throw new Error(resp.message || "Error changing rollset");
	}
};

export const changeWorkOrder = async ({
	workOrderId,
	workOrderName,
}: {
	workOrderId: number | null;
	workOrderName: string;
}): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "workOrderId", value: workOrderId },
		{ name: "workOrderName", value: workOrderName },
	];

	const resp = await apiService.callV2("[MILL].[ChangeWorkOrder]", parameters);
	if (!resp.ok) {
		throw new Error(resp.message || "Error changing work order");
	}
};

// ─── Supporting data for modals ──────────────────────────────────

export const getRecipesMeta = async (): Promise<RecipeMeta[]> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[MILL].[GetRecipesMeta]", []);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as RecipeMeta[];
	}
	throw new Error(resp.message || "Error fetching recipes");
};

export const getPassChangeReasons = async (): Promise<PassChangeReason[]> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[MILL].[GetPassChangeReasons]", []);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as PassChangeReason[];
	}
	throw new Error(resp.message || "Error fetching pass change reasons");
};

export const getRollsProduction = async (
	rollsetId: number,
): Promise<BarrelLayoutPass[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "rollsetId", value: rollsetId }];

	const resp = await apiService.callV2(
		"[ROLLS].[GetRollsProduction]",
		parameters,
	);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as BarrelLayoutPass[];
	}
	throw new Error(resp.message || "Error fetching rolls production");
};

export const getRollsets = async ({
	search,
	standId,
	statusCode,
}: {
	search?: string;
	standId?: number;
	statusCode?: string;
}): Promise<Rollset[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	if (search) parameters.push({ name: "Search", value: search });
	if (standId) parameters.push({ name: "StandId", value: standId });
	if (statusCode) parameters.push({ name: "StatusCode", value: statusCode });

	const resp = await apiService.callV2("[ROLLS].[GetRollsets]", parameters);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as Rollset[];
	}
	throw new Error(resp.message || "Error fetching rollsets");
};

export const getCompatibleRollsetIds = async ({
	standId,
	productSizeId,
}: {
	standId: number;
	productSizeId?: number;
}): Promise<number[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "StandId", value: standId }];
	if (productSizeId != null)
		parameters.push({ name: "ProductSizeId", value: productSizeId });

	const resp = await apiService.callV2(
		"[ROLLS].[GetCompatibleRollsetsForStand]",
		parameters,
	);
	if (resp.ok) {
		const rows = get(resp, "data.tables[0].rows", []) as {
			RollsetId: number;
		}[];
		return rows.map((r) => r.RollsetId);
	}
	throw new Error(resp.message || "Error fetching compatible rollsets");
};

export const getWorkOrders = async (params?: {
	search?: string;
	productSizeId?: number;
}): Promise<WorkOrder[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];
	if (params?.search) parameters.push({ name: "search", value: params.search });
	if (params?.productSizeId != null)
		parameters.push({ name: "productSizeId", value: params.productSizeId });

	const resp = await apiService.callV2("[MILL].[GetWorkOrders]", parameters);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as WorkOrder[];
	}
	throw new Error(resp.message || "Error fetching work orders");
};

export const getLastBilletsData = async (
	timestamp: string,
): Promise<LastBillet[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "timestamp", value: timestamp }];

	const resp = await apiService.callV2(
		"[ROLLS].[GetRollingMillSetupLastBilletsDataWeb]",
		parameters,
	);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as LastBillet[];
	}
	throw new Error(resp.message || "Error fetching last billets data");
};
